import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CalendarDays, Check, ChevronDown, ClipboardList, Minus, Plus, X } from 'lucide-react';
import WallTile from './WallTile';
import { Shrinkable, ShrinkButton } from './Shrinkable';
import examples from '../data/homeworkExamples.json';
import { homeworkLevelSummary } from '../data/assignmentHelp';
import { CATALOG, LEGACY_V4_LIMITS, LEVELS, V4_LIMITS, countOptions, levelsInRange, subjectOfTopic, topicById, topicsOf, domainLabel } from '../data/homeworkV4';
import { localDateIso } from '../data/checkpointSchedule';
import usePrefersReducedMotion from '../../utils/usePrefersReducedMotion';
import StudentPicker, { WhoPill } from './StudentPicker';
import { DatePopover } from './DatePicker';
import AddTopicMenu from './AddTopicMenu';
import './buffyCards.css';
import './practiceSetBox.css';

const StudentPreview = lazy(() => import('./HomeworkStudentPreview'));

/* One practice-set box for every place a teacher reviews a draft (Sandy
   2026-10-16: the Home chat's draft and the Assignments review should be the
   same box; keep the lilac glow, make the topics clear, let any topic open its
   example questions, and let the levels be edited). The caller owns the draft
   state and the footer (its own Assign button and messages).
   Calmer the same day (Sandy: "so much going on at once"; "teachers don't
   understand what the levels mean"): a topic row is its name and Examples;
   one ink + one gray; red only when something blocks Assign. Difficulty is
   chosen by example (DifficultyChooser), and level numbers show only under
   "Set exact levels". v4 practice sets only. */

const defaultDue = () => { const d = new Date(); d.setDate(d.getDate() + 7); return `${localDateIso(d)}T23:59`; };
const when = due => new Date(due).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
// The pill's short form: the time only when it isn't the usual end of day.
const dueLabel = due => { const t = new Date(due); const day = t.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }); return t.getHours() === 23 && t.getMinutes() === 59 ? day : `${day}, ${t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`; };
const rangeText = (min, max) => (min === max ? `Level ${min}` : `Levels ${min}–${max}`);
const sentence = t => (t ? `${t[0].toUpperCase()}${t.slice(1)}${/[.!?]$/.test(t) ? '' : '.'}` : '');

// Three difficulties a teacher can pick by example. Each is one level range
// for the whole set (the backend takes one range, not one per topic).
export const DIFFICULTIES = [
  { id: 'easier', label: 'Easier', min: 1, max: 3, aim: 2 },
  { id: 'medium', label: 'Medium', min: 3, max: 5, aim: 4 },
  { id: 'harder', label: 'Harder', min: 5, max: 7, aim: 6 },
];
const difficultyOf = (mode, range) => (mode === 'range' ? DIFFICULTIES.find(t => t.min === range.min && t.max === range.max) || null : null);
// What the set's difficulty is, in words. `picked` false: the teacher has
// not chosen yet (there is no default; Sandy 2026-10-16).
export const difficultyLabel = (mode, range, picked = true) => (!picked ? 'Not chosen yet' : mode !== 'range' ? 'Matched to each student'
  : `${difficultyOf(mode, range)?.label || rangeText(range.min, range.max)} for everyone`);
// A sample question's difficulty in the chooser's words (Levels 1-2 / 3-4 / 5-7).
export const bandWord = band => (band <= 2 ? 'Easier' : band <= 4 ? 'Medium' : 'Harder');

// homeworkLevelSummary speaks the legacy domain keys.
const LEVEL_GUIDE_KEY = {
  algebra: 'algebra', 'advanced-math': 'advanced', 'problem-solving-and-data-analysis': 'psda', 'geometry-and-trigonometry': 'geotrig',
  'information-and-ideas': 'info', 'craft-and-structure': 'craft', 'expression-of-ideas': 'expression', 'standard-english-conventions': 'conventions',
};
const forStudents = (domain, level) => {
  const s = homeworkLevelSummary(LEVEL_GUIDE_KEY[domain], level);
  return s ? s.replace(/^Level \d+: students /, 'For students who can ') : null;
};

// What the box itself shows as the reason Assign is off, for the caller's
// footer line: the full message stays next to its control in the box.
// limits: what the backend takes (homeworkV4 limitsOf; plan P7).
export function boxBlocker(d, check, limits = LEGACY_V4_LIMITS) {
  if (d.coverage === 'topics' && d.selected.length < limits.minTopics) return 'Add a topic above to assign.';
  if (d.levelPicked === false) return 'Choose how hard above to assign.';
  if (d.levelMode === 'range' && check && !check.ok) return 'Pick another difficulty above to assign.';
  return '';
}

// A topic's sample questions: the examples of every legacy concept that maps
// onto it (math topics can gather several), easiest first.
function topicExamples(subject, topicId) {
  const field = subject === 'both' ? subjectOfTopic(topicId) : subject;
  const map = CATALOG.legacyToTopic?.[field] || {};
  const ids = [...new Set([topicId, ...Object.keys(map).filter(k => map[k] === topicId)])];
  const seen = new Set();
  return ids.flatMap(id => examples[id] || [])
    .filter(q => !seen.has(q.recordID) && seen.add(q.recordID))
    .sort((a, b) => (a.band || 99) - (b.band || 99));
}
const graded = q => !q.illustrative && Number.isInteger(q.band);
// One example for a difficulty, closest to its middle level: from the set's
// own topics first, then other topics in the same domains (a grammar set
// shows grammar), then the field. `borrowed` marks one from outside the set.
function exampleFor(subject, topics, tier) {
  const fits = q => graded(q) && q.band >= tier.min && q.band <= tier.max;
  const best = list => list.filter(fits).sort((a, b) => Math.abs(a.band - tier.aim) - Math.abs(b.band - tier.aim))[0] || null;
  const from = list => best(list.flatMap(t => topicExamples(subject, t.id).map(q => ({ ...q, topic: t }))));
  const ids = new Set(topics.map(t => t.id));
  const doms = new Set(topics.map(t => t.domain));
  const own = from(topics);
  if (own) return own;
  const near = from(topicsOf(subject).filter(t => !ids.has(t.id) && doms.has(t.domain))) || from(topicsOf(subject).filter(t => !ids.has(t.id)));
  return near ? { ...near, borrowed: true } : null;
}
// The draft on its lilac tile: one white card, its name in the gray strip.
// Every state uses the same frame, so drafting -> ready -> assigning reads as
// one card changing.
// shrinkTitle (in Buffy's chat): the box can fold to a small tile named by it.
// Inside another <Shrinkable> (a chat dialog) the strip's shrink button
// folds that one instead.
// strip false (2026-10-16, the chat's set review: the reply already says it):
// no gray strip; the shrink button sits in the card's top-right corner.
export function DraftShell({ label, tag = 'Draft', busy = false, className = '', shrinkTitle = null, shrinkLabel = label, strip = true, children }) {
  const shell = <WallTile wall="lilac" className={`dc-tile ${className}`}>
    <div className={`bc-card dc-card${strip ? '' : ' dc-bare'}`} aria-busy={busy || undefined}>
      {strip ? <div className="sc-strip"><ClipboardList size={15} aria-hidden="true" />{label}<span className="sc-end">{tag && <span className="bc-tag">{tag}</span>}<ShrinkButton className="dc-shrink" /></span></div>
        : <ShrinkButton className="dc-corner" />}
      {children}
    </div>
  </WallTile>;
  return shrinkTitle ? <Shrinkable title={shrinkTitle} label={shrinkLabel}>{shell}</Shrinkable> : shell;
}

// The name wraps instead of hiding its end in a narrow card (2026-10-14):
// one row that grows with the text. It stays one line of text: Enter does
// nothing and pasted line breaks become spaces.
export function NameField({ value, onChange, ...rest }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let width = -1;
    const fit = () => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    // Refit only when the width changes; the height change itself is ignored.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [value]);
  return <textarea ref={ref} rows={1} value={value} onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
    onChange={e => onChange(e.target.value.replace(/\s*[\r\n]+\s*/g, ' '))} {...rest} />;
}

// The one red line when a difficulty leaves topics without questions. No
// level numbers: the teacher picked a difficulty, so it speaks of that.
export function DifficultyProblem({ subject, check, onRemove, busy = false }) {
  if (!check || check.ok) return null;
  const blocked = (check.details?.blockedTopicIds || []).map(id => topicById(subject, id)?.label).filter(Boolean);
  if (!blocked.length) return <p className="psb-error" role="alert">{sentence(check.error.replace(/(\d)-(\d)/g, '$1–$2'))}</p>;
  const one = blocked.length === 1;
  return <p className="psb-error" role="alert">{one ? `${blocked[0]} has` : `${blocked.length} topics have`} no questions this hard. Pick another difficulty{onRemove ? <>, or <button type="button" className="bc-text" disabled={busy} onClick={onRemove}>remove {one ? 'it' : 'them'}</button></> : null}.</p>;
}

// How hard the set is, in plain words (Sandy 2026-10-16: teachers don't know
// what the level numbers mean; give a simple explanation of each difficulty
// and let them expand a full example question). Nothing is chosen for the
// teacher: both options start unselected and Assign waits for a choice.
// "Matched to each student", or "Everyone gets the same difficulty": Easier / Medium /
// Harder, each with one line on who it suits and "See an example" (the full
// question as a student sees it, opened under the cards). "Set exact levels"
// keeps the From/To numbers for anyone who knows them.
const GENERIC_FIT = {
  easier: 'Easier questions, for students still building the basics.',
  medium: 'In-between questions, for students who have the basics.',
  harder: 'The hardest questions, for students ready to stretch.',
};
export function DifficultyChooser({ subject, topics, levelMode, levelRange, onChange: change, busy = false, chosen = true }) {
  const onChange = patch => change({ ...patch, levelPicked: true });
  const same = chosen && levelMode === 'range';
  const matched = chosen && levelMode !== 'range';
  const picked = chosen ? difficultyOf(levelMode, levelRange) : null;
  const [exact, setExact] = useState(same && !picked);
  const [showing, setShowing] = useState(null);
  const domains = [...new Set(topics.map(t => t.domain))];
  // One domain: that domain's own words for the level ("For students who can…").
  const fit = tier => (domains.length === 1 && forStudents(domains[0], tier.aim)) || GENERIC_FIT[tier.id];
  const pickRange = (min, max) => {
    const next = { min: min ?? levelRange.min, max: max ?? levelRange.max };
    if (min != null && next.min > next.max) next.max = next.min;
    if (max != null && next.max < next.min) next.min = next.max;
    onChange({ levelMode: 'range', levelRange: next });
  };
  const tiers = DIFFICULTIES.map(tier => {
    const usable = topics.some(t => levelsInRange(t, tier.min, tier.max).length);
    return { ...tier, usable, example: usable ? exampleFor(subject, topics, tier) : null };
  });
  const shown = tiers.find(t => t.id === showing && t.example);
  return <div className="dch" role="group" aria-label="How hard should it be?">
    <button type="button" aria-pressed={matched} className="dch-option" disabled={busy} onClick={() => onChange({ levelMode: 'student' })}>
      <span className="dch-title">{matched && <Check size={15} aria-hidden="true" />}Matched to each student</span>
      <span className="dch-sub">Each student gets questions at their own level.</span>
    </button>
    <div className={`dch-option dch-same${same ? ' is-on' : ''}`}>
      <span className="dch-title">{same && <Check size={15} aria-hidden="true" />}Everyone gets the same difficulty</span>
      <div className="dch-examples">
        {tiers.map(tier => {
          const on = picked?.id === tier.id;
          return <div key={tier.id} className={`dch-example${on ? ' is-on' : ''}${tier.usable ? '' : ' is-off'}`}>
            <button type="button" className="dch-pick" aria-pressed={on} disabled={busy || !tier.usable}
              onClick={() => { setExact(false); onChange({ levelMode: 'range', levelRange: { min: tier.min, max: tier.max } }); }}>
              <span className="dch-tier-name">{on && <Check size={14} aria-hidden="true" />}{tier.label}</span>
              <span className="dch-fit">{tier.usable ? fit(tier) : `These topics have no ${tier.label.toLowerCase()} questions.`}</span>
            </button>
            {tier.example && <button type="button" className="bc-text dch-see" aria-expanded={showing === tier.id}
              aria-label={`${showing === tier.id ? 'Hide' : 'See'} the ${tier.label.toLowerCase()} example`} onClick={() => setShowing(showing === tier.id ? null : tier.id)}>
              {showing === tier.id ? 'Hide example' : 'See an example'}</button>}
          </div>;
        })}
      </div>
      {shown && <div className="dch-shown">
        <p className="dch-shown-head">{shown.label} example · {shown.example.topic.label}{shown.example.borrowed ? ' (a related topic)' : ''}</p>
        <div className="psb-question">
          <Suspense fallback={<p className="psb-note" role="status">Opening the question…</p>}>
            <StudentPreview key={shown.example.recordID} question={shown.example} subject={subjectOfTopic(shown.example.topic.id) || subject} />
          </Suspense>
        </div>
      </div>}
      {!exact ? <button type="button" className="bc-text dch-exact-link" disabled={busy} onClick={() => setExact(true)}>Set exact levels</button>
        : <span className="dch-exact">
          <label>From<select aria-label="Lowest level" value={same ? levelRange.min : 3} disabled={busy} onChange={e => pickRange(Number(e.target.value), null)}>{LEVELS.map(l => <option key={l} value={l}>Level {l}</option>)}</select></label>
          <label>to<select aria-label="Highest level" value={same ? levelRange.max : 5} disabled={busy} onChange={e => pickRange(null, Number(e.target.value))}>{LEVELS.map(l => <option key={l} value={l}>Level {l}</option>)}</select></label>
        </span>}
    </div>
  </div>;
}

// A topic's examples, opened under its row: one tab per sample question, one
// plain line on who it fits, and the question as a student sees it.
export function TopicExamples({ subject, topic, range, onHide }) {
  const questions = topicExamples(subject, topic.id);
  const inSet = q => !range || !q.band || (q.band >= range.min && q.band <= range.max);
  const [index, setIndex] = useState(() => Math.max(0, questions.findIndex(inSet)));
  const hide = <button type="button" className="bc-text psb-hide" onClick={onHide}>Hide examples</button>;
  if (!questions.length) return <div className="psb-examples"><p className="psb-note">No example questions for this topic yet.</p>{hide}</div>;
  const question = questions[Math.min(index, questions.length - 1)];
  // Each sample says how hard it is, in the chooser's words; two at the same
  // difficulty get a number; one with no rating says so.
  const words = questions.map(q => (graded(q) ? bandWord(q.band) : null));
  const tabLabel = i => {
    const w = words[i];
    if (!w) return `Sample ${i + 1}`;
    return words.filter(x => x === w).length > 1 ? `${w} ${words.slice(0, i + 1).filter(x => x === w).length}` : w;
  };
  const guide = graded(question) ? forStudents(topic.domain, question.band) : null;
  return <div className="psb-examples">
    {questions.length > 1 && <div className="psb-tabs" role="group" aria-label={`${topic.label} example questions`}>
      {questions.map((q, i) => <button key={q.recordID} type="button" aria-pressed={i === index} onClick={() => setIndex(i)}>
        {tabLabel(i)}{range && graded(q) && !inSet(q) && <span className="psb-out">not in this set</span>}</button>)}
    </div>}
    {guide ? <p className="psb-guide">{guide}</p> : !graded(question) && <p className="psb-guide">This sample has no difficulty rating yet.</p>}
    <div className="psb-question">
      <Suspense fallback={<p className="psb-note" role="status">Opening the question…</p>}>
        <StudentPreview key={question.recordID} question={question} subject={subjectOfTopic(topic.id) || subject} />
      </Suspense>
    </div>
    {hide}
  </div>;
}

// One topic row: the name, "Examples" (opens its sample questions under the
// row), a red line when the difficulty leaves it empty, and an optional x.
// Shared by the new-set box and the assigned-set edit box.
export function TopicRow({ subject, topic: t, range, open, onToggle, onHide, onRemove, busy = false, blocked = false, toggleRef, rowRef }) {
  const none = range && !levelsInRange(t, range.min, range.max).length;
  return <li ref={rowRef} className={`psb-row${open ? ' is-open' : ''}${blocked || none ? ' is-blocked' : ''}`}>
    <div className="psb-line">
      <button type="button" ref={toggleRef} className="psb-toggle" aria-expanded={open} aria-label={`${t.label}. ${open ? 'Hide' : 'Show'} example questions`} onClick={onToggle}>
        <span className="psb-name">{t.label}</span>
        {none && <span className="psb-meta psb-warn">No questions this hard</span>}
        <span className="psb-see" aria-hidden="true">{open ? 'Hide' : 'Examples'}<ChevronDown size={15} /></span>
      </button>
      {onRemove && <button type="button" className="psb-x" aria-label={`Remove ${t.label}`} disabled={busy} onClick={onRemove}><X size={14} /></button>}
    </div>
    {open && <TopicExamples subject={subject} topic={t} range={range} onHide={onHide} />}
  </li>;
}

// The due-date pill: reads as words ("Due Fri, Oct 24"); a click opens the
// house date-and-time panel under it (a new due starts a week out), x clears
// it. Shared with the Assessments draft (2026-10-18) so both read and work the same.
export function DuePill({ due, onChange, busy = false, children = null }) {
  const [editing, setEditing] = useState(false);
  const pill = useRef(null);
  const btn = useRef(null);
  return <span ref={pill} className="dc-pill dc-due"><CalendarDays size={15} aria-hidden="true" />
    <button ref={btn} type="button" className="dc-plain" aria-haspopup="dialog" aria-expanded={editing} aria-label={due ? `Due ${when(due)}. Change the due date` : 'No due date. Add one'} disabled={busy}
      onClick={() => { if (!due) onChange(defaultDue()); setEditing(true); }}>{due ? `Due ${dueLabel(due)}` : 'No due date'}</button>
    {editing && <DatePopover anchor={pill} returnFocus={btn} mode="datetime" label="Due date" value={due} min={`${localDateIso()}T00:00`} onChange={onChange} onClose={() => setEditing(false)} />}
    {due && <button type="button" className="dc-x" aria-label="No due date" title="No due date" disabled={busy} onClick={() => { setEditing(false); onChange(''); }}><X size={13} /></button>}
    {children}</span>;
}

// limits: what the backend takes (homeworkV4 limitsOf, plan P7: 1+ topics and
// 20 questions when it says so); an older backend's 3 topics / 15 by default.
// also: the "Also give to" rows (assignments/AlsoGiveTo.jsx, flag
// PRACTICE_GROUPS), under everything else. hideWho: the set is not for this
// class ("Give to another class"), so its Who pill and students are left out.
export default function PracticeSetBox({ label = 'New practice set', tag = 'Draft', shrinkTitle = null, draft: d, onChange, onCoverage = onChange, check, limits = LEGACY_V4_LIMITS, students = null, busy = false, suggested = () => null, extra = null, also = null, hideWho = false, children }) {
  const [open, setOpen] = useState(null);
  const [showAll, setShowAll] = useState(false);
  // The teacher just chose Pick students: the cursor goes to the student search.
  const [pickFocus, setPickFocus] = useState(false);
  const chosen = d.levelPicked !== false;
  // Not chosen yet: the options are open, since the teacher has to pick one.
  const [choosing, setChoosing] = useState(!chosen);
  const reduced = usePrefersReducedMotion();
  const toggles = useRef({});
  const rows = useRef({});
  const addRef = useRef(null);
  const changeRef = useRef(null);
  // Where keyboard focus goes after a topic is removed or added: the row in
  // its place (or the add menu), so focus never drops to the page.
  const [focusNext, setFocusNext] = useState(null);
  useEffect(() => {
    if (!focusNext) return;
    (focusNext === 'add' ? addRef.current : toggles.current[focusNext])?.focus();
    setFocusNext(null);
  }, [focusNext]);
  // An opened topic comes into view (its examples can be long).
  useEffect(() => {
    if (open) rows.current[open]?.scrollIntoView?.({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  }, [open, reduced]);
  const range = chosen && d.levelMode === 'range' ? d.levelRange : null;

  const coverageTopics = d.coverage === 'topics' ? d.selected.map(id => topicById(d.subject, id)).filter(Boolean)
    : d.coverage === 'domain' ? topicsOf(d.subject).filter(t => t.domain === d.domainId)
      : topicsOf(d.subject);
  const collapsed = d.coverage === 'field' && !showAll;
  const removable = d.coverage === 'topics';
  const fewTopics = removable && d.selected.length < limits.minTopics;
  const need = limits.minTopics - d.selected.length;
  const counts = countOptions(limits);
  const domains = [...new Set(coverageTopics.map(t => t.domain))];
  const oneDomain = domains.length === 1 ? domains[0] : null;
  const others = removable ? topicsOf(d.subject).filter(t => !d.selected.includes(t.id)) : [];
  const nearby = fewTopics ? others.filter(t => !oneDomain || t.domain === oneDomain).slice(0, 3) : [];
  const blocked = check?.details?.blockedTopicIds || [];
  const levelProblem = range && check && !check.ok && !fewTopics;

  const add = id => { if (!id) return; onCoverage({ selected: [...d.selected, id] }); setFocusNext(id); };
  const remove = ids => {
    const left = d.selected.filter(x => !ids.includes(x));
    const at = d.selected.findIndex(x => ids.includes(x));
    if (ids.includes(open)) setOpen(null);
    onCoverage({ selected: left });
    setFocusNext(left[Math.min(at, left.length - 1)] || 'add');
  };
  const hide = id => { setOpen(null); toggles.current[id]?.focus(); };
  const step = dir => { const i = counts.indexOf(d.count); onChange({ count: counts[Math.min(counts.length - 1, Math.max(0, (i < 0 ? 0 : i) + dir))] }); };
  const doneChoosing = () => { setChoosing(false); setTimeout(() => changeRef.current?.focus(), 0); };

  return <DraftShell label={label} tag={tag} busy={busy} className="psb" shrinkTitle={shrinkTitle}>
    <div className="dc-body">
      <NameField className="bc-title dc-title" aria-label="Assignment name" value={d.title} maxLength={V4_LIMITS.titleMax} disabled={busy} onChange={title => onChange({ title, titleEdited: true })} />

      <section className="psb-topics" aria-label="What students will practice">
        {d.coverage !== 'topics' && <p className="psb-cover">{d.coverage === 'domain' ? `All of ${domainLabel(d.domainId)}` : `A mix of every ${d.subject === 'both' ? 'SAT' : d.subject === 'math' ? 'Math' : 'Reading & Writing'} topic`}</p>}
        {collapsed ? <button type="button" className="psb-all" onClick={() => setShowAll(true)}>See the {coverageTopics.length} topics<ChevronDown size={15} aria-hidden="true" /></button>
          : <ul className="psb-list">{coverageTopics.map(t => <TopicRow key={t.id} subject={d.subject} topic={t} range={range} busy={busy}
            open={open === t.id} onToggle={() => setOpen(open === t.id ? null : t.id)} onHide={() => hide(t.id)}
            onRemove={removable ? () => remove([t.id]) : null} blocked={blocked.includes(t.id)}
            toggleRef={el => { toggles.current[t.id] = el; }} rowRef={el => { rows.current[t.id] = el; }} />)}</ul>}
        {d.coverage === 'field' && showAll && <button type="button" className="psb-all psb-all-less" onClick={() => { setShowAll(false); setOpen(null); }}>Hide the topic list</button>}
        {fewTopics && <div className="psb-fix">
          <p>Add {need} more {need === 1 ? 'topic' : 'topics'}{oneDomain ? <>, or <button type="button" className="bc-text" disabled={busy} onClick={() => onCoverage({ coverage: 'domain', domainId: oneDomain, selected: [] })}>use all of {domainLabel(oneDomain)}</button></> : null}.</p>
          {nearby.length > 0 && <div className="dc-chips">{nearby.map(t => <button key={t.id} type="button" className="dc-chip dc-add" disabled={busy} onClick={() => add(t.id)}><Plus size={13} aria-hidden="true" />{t.label}</button>)}</div>}
        </div>}
        {removable && others.length > 0 && <AddTopicMenu topics={others} groupLabel={domainLabel} busy={busy} buttonRef={addRef} onAdd={add} />}
      </section>

      <section className="psb-levels" aria-label="Question levels">
        {!choosing ? <div className="psb-how"><span>How hard: <span className="psb-how-value">{difficultyLabel(d.levelMode, d.levelRange, chosen)}</span></span>
          <button type="button" ref={changeRef} className="bc-text" disabled={busy} onClick={() => setChoosing(true)}>{chosen ? 'Change' : 'Choose'}</button></div>
          : <>
            <div className="psb-how"><span className="psb-how-value">How hard should it be?</span>{chosen && <button type="button" className="bc-text" onClick={doneChoosing}>Done</button>}</div>
            <DifficultyChooser subject={d.subject} topics={coverageTopics} levelMode={d.levelMode} levelRange={d.levelRange} onChange={onChange} busy={busy} chosen={chosen} />
          </>}
        {levelProblem && <DifficultyProblem subject={d.subject} check={check} busy={busy} onRemove={removable && blocked.length ? () => remove(blocked) : null} />}
      </section>

      <div className="dc-pills">
        <span className="dc-pill dc-step">
          <button type="button" aria-label="Fewer questions" disabled={busy || d.count <= counts[0]} onClick={() => step(-1)}><Minus size={14} /></button>
          <output aria-live="polite">{d.count}</output><span>questions</span>
          <button type="button" aria-label="More questions" disabled={busy || d.count >= counts[counts.length - 1]} onClick={() => step(1)}><Plus size={14} /></button>
          {suggested('count')}</span>
        <DuePill due={d.due} onChange={due => onChange({ due })} busy={busy}>{suggested('due')}</DuePill>
        {!hideWho && <WhoPill who={d.who} studentIds={d.studentIds} total={students?.length || null} busy={busy}
          onChange={patch => { setPickFocus(patch.who === 'pick'); onChange(patch); }} />}
      </div>
      {!hideWho && d.who === 'pick' && <StudentPicker students={students} studentIds={d.studentIds} busy={busy} autoFocus={pickFocus} onChange={studentIds => onChange({ studentIds })} />}
      {extra}
      {also}
    </div>
    {children}
  </DraftShell>;
}
